import React, { useState, useRef } from "react";
import { toFroApi } from "../../api/toFroRegisterApi";
import {
  GlobalButton,
  GlobalTextField,
  GlobalSelectField,
  GlobalTextAreaField,
} from "../../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import UserSearch from "./UserSearch";
import { toMysqlDateTime } from "../../utils/mysqlDatetime";
import { datetimeLocalStartOfToday, datetimeLocalNow } from "../../utils/datetimeLocalBounds";
import "../../styles/createMovementForm.css";
import { masterSelectOptions } from "../../../memberships/utils/masterLookupUtils";

const CreateMovementForm = ({ masterData = { staff: [], purposes: [] }, onSuccess }) => {
  const [formData, setFormData] = useState({
    staff_user_id: "",
    designation: "",
    purpose_id: "",
    place_visited: "",
    out_time: "",
    remarks: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState({});
  const alertRef = useRef(null);

  const ALERT_ID = "create-movement-alert";

  const handleStaffUserIdChange = (id) => {
    const cleared = id === "" || id == null;
    setFormData((prev) => ({
      ...prev,
      staff_user_id: cleared ? "" : String(id),
      designation: cleared ? "" : prev.designation,
    }));
    if (cleared) {
      setErrorDetails((prev) => ({ ...prev, staff_user_id: "" }));
    }
  };

  const handleStaffUserSelect = (user) => {
    setFormData((prev) => ({
      ...prev,
      staff_user_id: String(user.id),
      designation: user.department_name || "",
    }));
    setErrorDetails((prev) => ({ ...prev, staff_user_id: "" }));
  };

  const setFormField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errorDetails[name]) {
      setErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleChange = (field, value) => {
    setFormField(field, value);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.staff_user_id) {
      errors.staff_user_id = "Search and select a staff or volunteer";
    }

    if (!formData.purpose_id) {
      errors.purpose_id = "Purpose is required";
    }

    if (!formData.place_visited.trim()) {
      errors.place_visited = "Place visited is required";
    }

    if (!formData.out_time) {
      errors.out_time = "Out time is required";
    } else {
      const min = datetimeLocalStartOfToday();
      const max = datetimeLocalNow();
      if (formData.out_time < min || formData.out_time > max) {
        errors.out_time = "Out time must be today's date and cannot be in the future";
      }
    }

    setErrorDetails(errors);
    return Object.keys(errors).length === 0;
  };

  const handleReset = () => {
    setError(null);
    setErrorDetails({});
    setFormData({
      staff_user_id: "",
      designation: "",
      purpose_id: "",
      place_visited: "",
      out_time: "",
      remarks: "",
    });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    setError(null);
    setErrorDetails({});

    if (!validateForm()) {
      setError("Please check the highlighted fields below.");
      setTimeout(() => alertRef.current?.focus(), 0);
      return;
    }

    setLoading(true);
    try {
      const movementData = {
        ...formData,
        staff_user_id: Number(formData.staff_user_id),
        purpose_id: Number(formData.purpose_id),
        out_time: toMysqlDateTime(formData.out_time),
      };

      await toFroApi.createMovement(movementData);

      setFormData({
        staff_user_id: "",
        designation: "",
        purpose_id: "",
        place_visited: "",
        out_time: "",
        remarks: "",
      });

      onSuccess?.("Movement created successfully!");
    } catch (err) {
      const status = err?.response?.status;
      const api = err?.response?.data;
      const msg = typeof api?.message === "string" ? api.message.trim() : "";
      const detail =
        (typeof api?.details === "string" && api.details.trim()) || msg || "";
      if (status === 409) {
        setError(
          msg ||
            "Duplicate entry: this staff member already has a movement with the same out time.",
        );
      } else {
        setError(detail || err.message || "Failed to create movement");
      }
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GlobalFormShell
      legend="Create Movement Entry"
      showAlert={!!error}
      alertText={error}
      alertId={ALERT_ID}
      formAlertRef={alertRef}
      actions={
        <>
          <GlobalButton
            type="submit"
            variant="primary"
            disabled={loading}
            onClick={handleSubmit}
            loading={loading}
          >
            {loading ? "Saving..." : "Create Movement"}
          </GlobalButton>
          <GlobalButton type="button" variant="ghost" onClick={handleReset} disabled={loading}>
            Clear form
          </GlobalButton>
        </>
      }
    >
      <div className="gff-field--full" style={{ marginBottom: "-0.5rem" }}>
        <h4
          style={{
            margin: "0.5rem 0",
            color: "var(--ub-teal-dark)",
            fontSize: "0.85rem",
            fontWeight: 700,
          }}
        >
          Staff Information
        </h4>
      </div>

      <UserSearch
        label="Staff / Volunteer Name"
        required
        error={errorDetails.staff_user_id}
        baseAlertId={ALERT_ID}
        value={formData.staff_user_id}
        onChange={handleStaffUserIdChange}
        onUserSelect={handleStaffUserSelect}
        placeholder="Type name or user ID (at least 2 characters)…"
      />

      <GlobalTextField
        id="designation"
        label="Designation"
        value={formData.designation}
        onChange={(value) => handleChange("designation", value)}
        placeholder="Filled when you select a user (from their role)"
        disabled={!!formData.staff_user_id}
        error={errorDetails.designation}
        baseAlertId={ALERT_ID}
      />

      <div className="gff-field--full" style={{ marginBottom: "-0.5rem" }}>
        <h4
          style={{
            margin: "0.5rem 0",
            color: "var(--ub-teal-dark)",
            fontSize: "0.85rem",
            fontWeight: 700,
          }}
        >
          Movement Details
        </h4>
      </div>

      <GlobalSelectField
        id="purpose_id"
        label="Purpose"
        value={formData.purpose_id}
        onChange={(value) => handleChange("purpose_id", value)}
        options={[
          ...masterSelectOptions(masterData?.purposes || [], {
            labelKey: "purpose_name",
            selectedValue: formData.purpose_id,
          }),
        ]}
        placeholderOption="Select Purpose"
        error={errorDetails.purpose_id}
        baseAlertId={ALERT_ID}
        required
      />

      <GlobalTextField
        id="place_visited"
        label="Place Visited"
        value={formData.place_visited}
        onChange={(value) => handleChange("place_visited", value)}
        placeholder="Enter place visited"
        error={errorDetails.place_visited}
        baseAlertId={ALERT_ID}
        required
      />

      <GlobalTextField
        id="out_time"
        label="Out Time"
        type="datetime-local"
        value={formData.out_time}
        onChange={(value) => handleChange("out_time", value)}
        error={errorDetails.out_time}
        baseAlertId={ALERT_ID}
        required
        min={datetimeLocalStartOfToday()}
        max={datetimeLocalNow()}
      />

      <GlobalTextAreaField
        id="remarks"
        label="Remarks"
        value={formData.remarks}
        onChange={(value) => handleChange("remarks", value)}
        placeholder="Enter any additional remarks about this movement"
        rows={3}
        classNames={{ fieldWrap: "gff-field gff-field--full" }}
      />
    </GlobalFormShell>
  );
};

export default CreateMovementForm;
